import { useCallback, useState } from "react";
import type { SortDirectionType } from "@rio-cloud/rio-uikit/SortDirection";

export const TABLE_SORT_STORAGE_PREFIX = "tco:table-sort:";

export interface StoredTableSort<TKey extends string> {
  sortKey: TKey | null;
  sortDirection: SortDirectionType;
}

export interface UseStoredTableSortOptions<TKey extends string> {
  storageKey: string;
  allowedSortKeys: readonly TKey[];
  defaultSortKey: TKey | null;
  defaultSortDirection: SortDirectionType;
}

const isSortDirection = (value: unknown): value is SortDirectionType => value === "asc" || value === "desc";

const readStored = (key: string): Record<string, unknown> | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const writeStored = <TKey extends string>(key: string, sort: StoredTableSort<TKey>) => {
  try {
    localStorage.setItem(key, JSON.stringify(sort));
  } catch {
    return;
  }
};

const reconcile = <TKey extends string>(
  stored: Record<string, unknown> | null,
  { allowedSortKeys, defaultSortKey, defaultSortDirection }: UseStoredTableSortOptions<TKey>
): StoredTableSort<TKey> => {
  const fallback = { sortKey: defaultSortKey, sortDirection: defaultSortDirection };

  if (!stored || !isSortDirection(stored.sortDirection)) return fallback;

  if (stored.sortKey === null) return { sortKey: null, sortDirection: stored.sortDirection };

  const isKnownKey = typeof stored.sortKey === "string" && allowedSortKeys.includes(stored.sortKey as TKey);

  return isKnownKey ? { sortKey: stored.sortKey as TKey, sortDirection: stored.sortDirection } : fallback;
};

export const useStoredTableSort = <TKey extends string>(options: UseStoredTableSortOptions<TKey>) => {
  const storageKey = `${TABLE_SORT_STORAGE_PREFIX}${options.storageKey}`;
  const [sort, setSort] = useState<StoredTableSort<TKey>>(() => reconcile(readStored(storageKey), options));

  const update = useCallback(
    (patch: Partial<StoredTableSort<TKey>>) => {
      setSort((current) => {
        const next = { ...current, ...patch };
        writeStored(storageKey, next);
        return next;
      });
    },
    [storageKey]
  );

  const setSortKey = useCallback((sortKey: TKey | null) => update({ sortKey }), [update]);

  const setSortDirection = useCallback((sortDirection: SortDirectionType) => update({ sortDirection }), [update]);

  return { sortKey: sort.sortKey, sortDirection: sort.sortDirection, setSortKey, setSortDirection };
};
